import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, MaskedLine, Eyebrow, EASE_OUT } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_executive-hire-pro/artifacts/mww4f41g_c.webp";

const SERVICE_OPTIONS = [
  "Executive Search", "Retained Search", "Permanent Hiring", "Contract Hiring",
  "IT Staff Augmentation", "HR Advisory", "Startup HR Services", "Training", "Other",
];

const REGIONS = [
  { name: "India", desc: "Headquarters & core delivery market across technology hubs." },
  { name: "Asia", desc: "Leadership and specialist mandates across key Asian markets." },
  { name: "EMEA", desc: "Cross-border executive search and talent intelligence." },
];

const TEAM = [
  {
    img: "/team/consultant-1.png",
    name: "Pushpanjali Konhor",
    role: "Director — Client Management & Operations",
    bio: "Leads client relationships and delivery operations across mandates — known for a proactive, precise approach through every stage of the search and interview process.",
    id: "pushpanjali-konhor",
  },
  {
    img: "/team/consultant-2.png",
    name: "Jayanti Banerjee",
    role: "Sr. Client Manager & Operations",
    bio: "Drives candidate engagement as a subject-matter partner — guiding candidates through client context, fitment strategy and cultural orientation, and keeping relationships at the center of every mandate.",
    id: "jayanti-banerjee",
  },
  {
    img: "/team/advisory-leader.png",
    name: "Siv Sankar",
    role: "Sr. HR Advisory Consultant",
    bio: "A seasoned HR leader with 20+ years in Human Resources and 10+ years in leadership roles — organizational development, talent engagement, process transformation and strategic HR. Played a key role in building Ericsson's R&D unit from the ground up and turning HR functions into profit centers.",
    id: "siv-sankar",
  },
];

const EMPTY_FORM = { name: "", company: "", designation: "", email: "", phone: "", service: "Executive Search", message: "" };

const inputCls =
  "w-full border border-line-medium bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink-faded outline-none transition-colors duration-300 focus:border-gold";

const AboutContact = () => {
  const { hash } = useLocation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (hash === "#contact") {
      const t = setTimeout(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [hash]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSubmitted(true);
      setForm(EMPTY_FORM);
      toast.success("Requirement received. Our advisory team will respond shortly.");
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="about-contact-page">
      {/* HERO */}
      <section className="border-b border-line pt-36 lg:pt-44" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-6 pb-16 lg:px-10 lg:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <Eyebrow>About Ally eXecutive HR</Eyebrow>
          </motion.div>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Recruitment is our profession.</MaskedLine>
            <MaskedLine delay={0.4}>
              Understanding business is <span className="italic text-gold-dark">our advantage.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE_OUT }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-ink-muted sm:text-lg"
          >
            A specialist Executive Search, Recruitment Consulting and HR
            Advisory firm helping organizations identify, attract and retain
            exceptional talent across leadership, specialist and
            technology-driven roles.
          </motion.p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="about-founder-section">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" aria-hidden />
                <img
                  src={FOUNDER_IMG}
                  alt="Shan Kaul, Founder & Director of Ally eXecutive HR"
                  className="relative aspect-[4/5] w-full object-cover"
                  data-testid="about-founder-image"
                />
                <div className="absolute -bottom-6 -right-6 bg-ink px-6 py-5 text-paper shadow-2xl">
                  <p className="font-serif text-lg">Shan Kaul</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Founder & Director</p>
                </div>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-7 lg:pl-6">
            <FadeUp delay={0.1}>
              <Eyebrow>The Founder</Eyebrow>
              <h2 className="mt-5 font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
                "Recruitment should be about understanding people and businesses —
                not simply matching keywords."
              </h2>
              <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink-muted">
                With extensive experience in Talent Acquisition, Executive Search
                and HR Consulting, Shan Kaul founded Ally eXecutive HR on that
                belief. Over the years, the firm has developed deep expertise
                across technology, SaaS, InsurTech, MarTech, AdTech and
                specialized leadership hiring.
              </p>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-muted">
                The development of RoleSense™ represents the next evolution of
                that philosophy — combining years of recruitment experience with
                technology designed to make talent decisions more contextual and
                intelligent.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* LEADERSHIP TEAM */}
      <section className="border-b border-line bg-paper-deep py-20 lg:py-28" data-testid="team-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Eyebrow>The Team</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Senior consultants behind every mandate.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-muted">
              A compact, senior team — recruiters and HR leaders who personally
              engage every candidate and every client.
            </p>
          </FadeUp>
          <div className="mt-14 grid gap-px border border-line-medium bg-line-medium md:grid-cols-3">
            {TEAM.map((m, i) => (
              <FadeUp key={m.id} delay={0.07 * i} className="group bg-paper p-8" data-testid={`team-card-${m.id}`}>
                <div className="overflow-hidden bg-paper-deep">
                  <img
                    src={m.img}
                    alt={m.role}
                    className="aspect-[4/4] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                    data-testid={`team-photo-${m.id}`}
                  />
                </div>
                <h3 className="mt-6 font-serif text-xl text-ink">{m.name}</h3>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-gold-dark">{m.role}</p>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">{m.bio}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="border-y border-line bg-paper-deep py-20 lg:py-28" data-testid="regions-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Eyebrow>Regional Focus</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Building leadership teams across India, Asia & EMEA.
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-line-medium bg-line-medium lg:grid-cols-3">
            {REGIONS.map((r, i) => (
              <FadeUp key={r.name} delay={0.07 * i} className="bg-paper p-9">
                <p className="font-mono text-xs text-gold-dark">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-serif text-2xl text-ink">{r.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">{r.desc}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="mx-auto max-w-7xl scroll-mt-28 px-6 py-24 lg:px-10 lg:py-32" data-testid="contact-section">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <Eyebrow>Contact</Eyebrow>
              <h2 className="mt-5 font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
                Tell us what you need.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-ink-muted">
                Whether you are hiring a senior leader, building a technology
                team, scaling a startup or strengthening your HR function —
                share your requirement and our advisory team will respond.
              </p>
              <div className="mt-10 space-y-5 border-t border-line pt-8">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Email</p>
                  <p className="mt-1.5 text-sm font-medium text-ink" data-testid="contact-email">s-kaul@ally-executive.com</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Phone</p>
                  <p className="mt-1.5 text-sm font-medium text-ink" data-testid="contact-phone">India +91 96543 83898 · UAE +971 58 145 1549</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Offices</p>
                  <p className="mt-1.5 text-sm font-medium text-ink" data-testid="contact-offices">Noida · Bangalore (India) · Ajman Free Zone (UAE)</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Services</p>
                  <p className="mt-1.5 text-sm font-medium text-ink">Executive Search · Recruitment · Consulting · Advisory</p>
                </div>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              {submitted ? (
                <div className="flex h-full min-h-[420px] flex-col items-center justify-center border border-gold bg-paper-card p-12 text-center" data-testid="contact-success-message">
                  <CheckCircle2 className="h-12 w-12 text-gold-dark" />
                  <h3 className="mt-6 font-serif text-2xl text-ink">Requirement received.</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-muted">
                    Thank you for reaching out. A senior consultant from Ally
                    eXecutive HR will review your requirement and respond shortly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
                    data-testid="contact-submit-another-button"
                  >
                    Submit Another Requirement
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="border border-line-medium bg-paper-card p-8 lg:p-12" data-testid="contact-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="cf-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Name *</label>
                      <input id="cf-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your full name" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label htmlFor="cf-company" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Company *</label>
                      <input id="cf-company" required value={form.company} onChange={update("company")} className={inputCls} placeholder="Organization name" data-testid="contact-company-input" />
                    </div>
                    <div>
                      <label htmlFor="cf-designation" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Designation</label>
                      <input id="cf-designation" value={form.designation} onChange={update("designation")} className={inputCls} placeholder="Your role" data-testid="contact-designation-input" />
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Business Email *</label>
                      <input id="cf-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" data-testid="contact-email-input" />
                    </div>
                    <div>
                      <label htmlFor="cf-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Phone</label>
                      <input id="cf-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="+91 ..." data-testid="contact-phone-input" />
                    </div>
                    <div>
                      <label htmlFor="cf-service" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Service Required *</label>
                      <select id="cf-service" required value={form.service} onChange={update("service")} className={inputCls} data-testid="contact-service-select">
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faded">Tell us about your requirement *</label>
                      <textarea id="cf-message" required rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} placeholder="Role, team context, timelines, success criteria..." data-testid="contact-message-textarea" />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group mt-8 inline-flex w-full items-center justify-center gap-2 border border-ink bg-ink px-8 py-4 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-gold hover:border-gold hover:text-ink disabled:opacity-60 sm:w-auto"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? "Submitting..." : "Submit Requirement"}
                    {!submitting && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
                  </button>
                </form>
              )}
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutContact;
